import { TableColumnsType } from "antd";
import _ from "lodash";
import { FC, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import exportTable from "../../../lib/exportTable";
import { IEnrolledStudentByMonth } from "../../../models/statistics.model";
import DownloadButton from "../../DownloadButton";
import moment from "moment";
import { formatOneDecimal } from "../../../lib/util";

interface IProps {
  res: any;
  learningYearCode: string|undefined;
  semesterType: string;
}

const TableContainer: FC<IProps> = ({
  res,
  learningYearCode,
  semesterType
}) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<any>([]);
  const [lSemesterType, setLSemesterType] = useState("RG");

  useEffect(() => {
    if (res.length) {
      let _data: any = [];
      // @ts-ignore
      res.at(0).data.forEach(campus => {
        campus.monthInfo.forEach((month: any) => {
          const item: any = {
            campusCode: campus.campusCode,
            campusKorName: campus.campusKorName,
            campusEngName: campus.campusEngName,
            monthName: month.monthName,
            monthEngName: month.monthEngName,
            reCount: month.reCount,
            s1Count: month.s1Count,
            s2Count: month.s2Count,
          };

          const prevYearData = res.at(1)?.data?.find((prevCampus: any) => prevCampus.campusCode === campus.campusCode)?.monthInfo?.find((prevMonth: any) => prevMonth.monthName === month.monthName);
          if (prevYearData) {
            item.prevReCount = prevYearData.reCount;
            item.prevS1Count = prevYearData.s1Count;
            item.prevS2Count = prevYearData.s2Count;
            item.reDiffPercent = (item.reCount - item.prevReCount) / item.prevReCount * 100;
            item.reDiffPercent = isFinite(item.reDiffPercent) ? Math.round(item.reDiffPercent) : null;
            item.s1DiffPercent = (item.s1Count - item.prevS1Count) / item.prevS1Count * 100;
            item.s1DiffPercent = isFinite(item.s1DiffPercent) ? Math.round(item.s1DiffPercent) : null;
            item.s2DiffPercent = (item.s2Count - item.prevS2Count) / item.prevS2Count * 100;
            item.s2DiffPercent = isFinite(item.s2DiffPercent) ? Math.round(item.s2DiffPercent) : null;
          } else {
            item.prevReCount = null;
            item.prevS1Count = null;
            item.prevS2Count = null;
            item.reDiffPercent = null;
            item.s1DiffPercent = null;
            item.s2DiffPercent = null;
          }

          _data.push(item);
        });

        const total: any = {
          campusCode: 'total',
          campusKorName: campus.campusKorName,
          campusEngName: campus.campusEngName,
          monthName: t('statistics.studentEnrollMonth.column8'),
          monthEngName: t('statistics.studentEnrollMonth.column8'),
          reCount: _.sumBy(campus.monthInfo, 'reCount'),
          s1Count: _.sumBy(campus.monthInfo, 's1Count'),
          s2Count: _.sumBy(campus.monthInfo, 's2Count'),
          prevReCount: _.sumBy(campus.monthInfo, 'prevReCount'),
          prevS1Count: _.sumBy(campus.monthInfo, 'prevS1Count'),
          prevS2Count: _.sumBy(campus.monthInfo, 'prevS2Count'),
          reDiffPercent: null,
          s1DiffPercent: null,
          s2DiffPercent: null,
        }
        _data.push(total);

        const mean: any = {
          campusCode: 'total',
          campusEngName: campus.campusEngName,
          campusKorName: campus.campusKorName,
          monthName: t('statistics.studentEnrollMonth.column9'),
          monthEngName: t('statistics.studentEnrollMonth.column9'),
          reCount: formatOneDecimal(_.meanBy(campus.monthInfo, 'reCount')),
          s1Count: formatOneDecimal(_.meanBy(campus.monthInfo, 's1Count')),
          s2Count: formatOneDecimal(_.meanBy(campus.monthInfo, 's2Count')),
          prevReCount: formatOneDecimal(_.meanBy(campus.monthInfo, 'prevReCount')),
          prevS1Count: formatOneDecimal(_.meanBy(campus.monthInfo, 'prevS1Count')),
          prevS2Count: formatOneDecimal(_.meanBy(campus.monthInfo, 'prevS2Count')),
        }
        if (mean.prevReCount) {
          mean.reDiffPercent = Math.round((mean.reCount - mean.prevReCount) / mean.prevReCount * 100);
        } else {
          mean.reDiffPercent = null;
        }
        if (mean.prevS1Count) {
          mean.s1DiffPercent = Math.round((mean.s1Count - mean.prevS1Count) / mean.prevS1Count * 100);
        } else {
          mean.s1DiffPercent = null;
        }
        if (mean.prevS2Count) {
          mean.s2DiffPercent = Math.round((mean.s2Count - mean.prevS2Count) / mean.prevS2Count * 100);
        } else {
          mean.s2DiffPercent = null;
        }
        _data.push(mean);
      });

      setData(_data);

      setLSemesterType(semesterType);
    }
  }, [res]);

  const onExportTable = () => {
    const _data: any = _.cloneDeep(data);
    _data.forEach((item: any) => {
      item.reCount = item.reCount || 0;
      item.s1Count = item.s1Count || 0;
      item.s2Count = item.s2Count || 0;
      item.prevReCount = item.prevReCount || 0;
      item.prevS1Count = item.prevS1Count || 0;
      item.prevS2Count = item.prevS2Count || 0;
      item.reDiffPercent = item.reDiffPercent || '-';
      item.s1DiffPercent = item.s1DiffPercent || '-';
      item.s2DiffPercent = item.s2DiffPercent || '-';
    });
    const currentDate = moment(new Date());
    exportTable(columns, _data, `${t('statistics.studentEnrolledMonth.1')}_(${currentDate.format('YYYYMMDD')})`, t('statistics.studentEnrolledMonth.1'), [12, 10, 15, 15, 15, 15, 15, 15, 15, 15, 15]);
  }
  
  const columns: TableColumnsType<IEnrolledStudentByMonth> = [
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusKorName',
      align: 'center',
    },
    {
      title: t('student.student.column48'),
      dataIndex: i18n.language === 'ko' ? 'monthName' : 'monthEngName',
      align: 'center',
    },
    {
      title: `(${learningYearCode}) ${t('statistics.studentEnrollMonth.column1')}`,
      dataIndex: 'reCount',
      align: 'center',
    },
    {
      title: `(${learningYearCode}) ${t('statistics.studentEnrollMonth.column2')}`,
      dataIndex: 's1Count',
      align: 'center',
    },
    {
      title: `(${learningYearCode}) ${(lSemesterType==='RG') ? t('statistics.studentEnrollMonth.column3') : t('statistics.attendance.column10')}`,
      dataIndex: 's2Count',
      align: 'center',
    },
    {
      title: `(${Number(learningYearCode) - 1}) ${t('statistics.studentEnrollMonth.column1')}`,
      dataIndex: 'prevReCount',
      align: 'center',
    },
    {
      title: `(${Number(learningYearCode) - 1}) ${t('statistics.studentEnrollMonth.column2')}`,
      dataIndex: 'prevS1Count',
      align: 'center',
    },
    {
      title: `(${Number(learningYearCode) - 1}) ${(lSemesterType==='RG') ? t('statistics.studentEnrollMonth.column3') : t('statistics.attendance.column10')}`,
      dataIndex: 'prevS2Count',
      align: 'center',
    },
    {
      title: `${t('statistics.studentEnrollMonth.column4')}`,
      dataIndex: 'reDiffPercent',
      align: 'center',
    },
    {
      title: `${t('statistics.studentEnrollMonth.column5')}`,
      dataIndex: 's1DiffPercent',
      align: 'center',
    },
    {
      title: `${(lSemesterType==='RG') ? t('statistics.studentEnrollMonth.column6') : '(' + t('statistics.studentEnrollMonth.column7') + ') ' + t('statistics.attendance.column10') }`,
      dataIndex: 's2DiffPercent',
      align: 'center',
    },
  ];

  return (
    <>
      <div className="comm-table__area line-type">
        <table>
          <thead>
            <tr>
              <th rowSpan={2}>{t('common.menu.campus')}</th>
              <th rowSpan={2}>{t('student.student.column48')}</th>
              <th colSpan={3}>{learningYearCode}</th>
              <th colSpan={3}>{Number(learningYearCode) - 1}</th>
              <th colSpan={3}>{t('statistics.studentEnrollMonth.column7')}</th>
            </tr>
            <tr>
              <th>{t('statistics.studentEnrollMonth.column1')}</th>
              <th>{t('statistics.studentEnrollMonth.column2')}</th>
              <th>{(lSemesterType==='RG') ? t('statistics.studentEnrollMonth.column3') : t('statistics.attendance.column10')}</th>
              <th>{t('statistics.studentEnrollMonth.column1')}</th>
              <th>{t('statistics.studentEnrollMonth.column2')}</th>
              <th>{(lSemesterType==='RG') ? t('statistics.studentEnrollMonth.column3') : t('statistics.attendance.column10')}</th>
              <th>{t('statistics.studentEnrollMonth.column1')}</th>
              <th>{t('statistics.studentEnrollMonth.column2')}</th>
              <th>{(lSemesterType==='RG') ? t('statistics.studentEnrollMonth.column3') : t('statistics.attendance.column10')}</th>
            </tr>
          </thead>
          <tbody>
          {data.map((item: any) => (
            <tr className={item.campusCode === 'total' ? 'tfoot' : ''}>
              <td>{item.campusKorName}</td>
              <td>{i18n.language === 'ko' ? item.monthName : item.monthEngName}</td>
              <td>{item.reCount || 0}</td>
              <td>{item.s1Count || 0}</td>
              <td>{item.s2Count || 0}</td>
              <td>{item.prevReCount || 0}</td>
              <td>{item.prevS1Count || 0}</td>
              <td>{item.prevS2Count || 0}</td>
              <td>{item.reDiffPercent ? `${item.reDiffPercent}%` : '-'}</td>
              <td>{item.s1DiffPercent ? `${item.s1DiffPercent}%` : '-'}</td>
              <td>{item.s2DiffPercent ? `${item.s2DiffPercent}%` : '-'}</td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      <div className="comm-bx__space">
        <DownloadButton onClick={onExportTable} />
      </div>
    </>
  );
}

export default TableContainer;